import React, { useEffect, useMemo, useState } from "react";
import { Sparkles, Sun, Waves, Star, Moon, Loader2, PenLine, Share2, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const BIO_KEY = "ep_biorhythm_dob";

/** Colour band for the arc + score number based on score value. */
function bandColour(score) {
  if (score >= 60) return { stroke: "var(--gold)",       ring: "rgba(212,162,76,0.85)" };
  if (score >= 40) return { stroke: "var(--rose-gold)",  ring: "rgba(229,168,124,0.85)" };
  return             { stroke: "#e26964",                ring: "rgba(226,105,100,0.85)" };
}

/**
 * Semi-circular SVG gauge, 0-100. Uses a coloured arc that fills according
 * to the score, plus a big numeric label and a one-line descriptor.
 */
function Gauge({ score, descriptor }) {
  // Semi-circle geometry
  const size = 340;
  const cx = size / 2;
  const cy = size * 0.7;
  const r = size * 0.42;
  const startAngle = 180; // left
  const endAngle = 0;     // right
  const trackPath = arcPath(cx, cy, r, startAngle, endAngle);
  const valueAngle = 180 - (Math.max(0, Math.min(100, score)) / 100) * 180;
  const valuePath = arcPath(cx, cy, r, startAngle, valueAngle);
  const colour = bandColour(score);

  return (
    <div className="relative flex flex-col items-center" data-testid="cosmic-gauge">
      <svg viewBox={`0 0 ${size} ${size * 0.85}`} width="100%" style={{ maxWidth: 380 }} aria-hidden>
        <defs>
          <linearGradient id="gauge-arc" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#e26964" />
            <stop offset="50%" stopColor="#e5a87c" />
            <stop offset="100%" stopColor="#d4a24c" />
          </linearGradient>
        </defs>
        {/* Track */}
        <path d={trackPath} fill="none" stroke="rgba(245,230,200,0.10)" strokeWidth="18" strokeLinecap="round" />
        {/* Value arc */}
        <path d={valuePath} fill="none" stroke="url(#gauge-arc)" strokeWidth="18" strokeLinecap="round" />
        {/* Score digits */}
        <text x={cx} y={cy - 14} textAnchor="middle"
              fill="var(--parchment)"
              style={{ fontFamily: "Georgia, serif", fontSize: 96, fontWeight: 400 }}
              data-testid="cosmic-score-number">
          {score}
        </text>
        <text x={cx} y={cy + 30} textAnchor="middle"
              fill={colour.stroke}
              style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 20 }}>
          / 100
        </text>
      </svg>
      <div
        className="mt-1 text-center font-serif-italic text-sm md:text-base"
        style={{ color: colour.stroke }}
        data-testid="cosmic-descriptor"
      >
        {descriptor}
      </div>
    </div>
  );
}

function arcPath(cx, cy, r, aStartDeg, aEndDeg) {
  const rad = (d) => (d * Math.PI) / 180;
  const x1 = cx + r * Math.cos(rad(aStartDeg));
  const y1 = cy - r * Math.sin(rad(aStartDeg));
  const x2 = cx + r * Math.cos(rad(aEndDeg));
  const y2 = cy - r * Math.sin(rad(aEndDeg));
  // Sweep the shorter way (0 = counter-clockwise on our screen)
  const sweep = 0;
  const largeArc = Math.abs(aStartDeg - aEndDeg) > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} ${sweep} ${x2} ${y2}`;
}


/**
 * Renders the "Share your score" button. On mobile with Web Share API + File
 * support, shares the PNG directly to Instagram / TikTok / WhatsApp. Falls
 * back to a plain download link on desktop.
 */
function ShareScoreButton({ dob, score, descriptor }) {
  const [busy, setBusy] = useState(false);

  const share = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND}/api/cosmic-score/share-card?dob=${encodeURIComponent(dob)}`);
      if (!res.ok) throw new Error("share card fetch failed");
      const blob = await res.blob();
      const file = new File([blob], `earth-pulse-score-${score}.png`, { type: "image/png" });
      const shareData = {
        title: "My Cosmic Score today",
        text: `My Earth Pulse cosmic score today is ${score} — ${descriptor}. Get yours at earthpulse.store`,
        files: [file],
      };
      if (navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        toast.success("Shared ✨");
      } else {
        // Desktop / unsupported — trigger download
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `earth-pulse-score-${score}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success("Downloaded — post it to your Story");
      }
    } catch (e) {
      toast.error("Couldn't grab your score card — try again in a moment");
    } finally {
      setBusy(false);
    }
  };

  const canShare = typeof navigator !== "undefined" && !!navigator.canShare;

  return (
    <button
      type="button"
      onClick={share}
      disabled={busy}
      data-testid="cosmic-share-btn"
      className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium text-sm disabled:opacity-60"
    >
      {canShare ? <Share2 size={14} /> : <Download size={14} />}
      {busy ? "Preparing…" : (canShare ? "Share your score" : "Download score card")}
    </button>
  );
}


export default function CosmicScoreDashboard() {
  const [dob, setDob] = useState(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem(BIO_KEY) || "";
  });
  const [pendingDob, setPendingDob] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!dob) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`${BACKEND}/api/cosmic-score?dob=${encodeURIComponent(dob)}`)
      .then((r) => r.ok ? r.json() : Promise.reject(new Error("failed")))
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load your cosmic score just now.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [dob]);

  const savePersonalDob = (e) => {
    e?.preventDefault();
    if (!pendingDob) return;
    try { localStorage.setItem(BIO_KEY, pendingDob); } catch { /* ignore quota */ }
    setDob(pendingDob);
  };

  // ── Empty state — no DOB yet ─────────────────────────────────────
  if (!dob) {
    return (
      <section
        data-testid="cosmic-score-empty"
        className="rounded-2xl border border-[color:var(--gold)]/25 p-6 md:p-10 text-center"
        style={{ background: "linear-gradient(135deg, rgba(212,162,76,0.06), rgba(229,168,124,0.03))" }}
      >
        <Sparkles size={20} className="text-[color:var(--gold)] mx-auto" />
        <h2 className="mt-3 font-serif-display text-[color:var(--parchment)] text-3xl md:text-5xl leading-[1.05] tracking-tight">
          Your <span className="font-serif-italic text-[color:var(--gold)]">Cosmic Score</span> lives here.
        </h2>
        <p className="mt-4 max-w-2xl mx-auto text-[color:var(--parchment-soft)] text-base md:text-lg font-sans-body leading-relaxed">
          A single, honest number for how the field is meeting <em className="font-serif-italic text-[color:var(--gold)] not-italic">you</em> today — Earth&apos;s heartbeat, the Sun&apos;s mood, and your own biorhythm, woven together.
        </p>
        <p className="mt-3 max-w-2xl mx-auto font-serif-italic text-[color:var(--rose-gold)] text-sm md:text-base">
          We only reveal it once you enter your birthday — no personalised content until it&apos;s truly yours.
        </p>
        <form onSubmit={savePersonalDob} className="mt-6 flex flex-col sm:flex-row items-stretch gap-3 max-w-md mx-auto">
          <Input
            type="date"
            value={pendingDob}
            onChange={(e) => setPendingDob(e.target.value)}
            required
            className="bg-white border-slate-300 rounded-lg h-12 text-slate-900 font-sans-body flex-1"
            data-testid="cosmic-empty-dob"
          />
          <Button
            type="submit"
            disabled={!pendingDob}
            data-testid="cosmic-empty-submit"
            className="h-12 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium disabled:opacity-40"
          >
            Reveal my score
          </Button>
        </form>
        <p className="mt-3 font-serif-italic text-[color:var(--text-muted)] text-xs">
          Kept on this device only. No account. No selling of data.
        </p>
      </section>
    );
  }

  // ── Loading state ─────────────────────────────────────────────
  if (loading || !data) {
    return (
      <section
        data-testid="cosmic-score-loading"
        className="rounded-2xl border border-[color:var(--gold)]/25 p-10 flex flex-col items-center justify-center gap-3 min-h-[260px]"
        style={{ background: "rgba(10, 17, 40, 0.4)" }}
      >
        <Loader2 size={20} className="text-[color:var(--gold)] animate-spin" />
        <span className="font-serif-italic text-[color:var(--text-secondary)] text-sm">
          {error ? error : "reading the field for you…"}
        </span>
      </section>
    );
  }

  const sw = data.space_weather || {};
  const sc = data.schumann || {};
  const mo = data.moon || null;
  const ho = data.horoscope || {};

  return (
    <section data-testid="cosmic-score" className="space-y-5">
      {/* Gauge card */}
      <div
        className="rounded-2xl border border-[color:var(--gold)]/25 p-6 md:p-8"
        style={{ background: "linear-gradient(135deg, rgba(212,162,76,0.05), rgba(229,168,124,0.02))" }}
      >
        <div className="flex flex-col md:flex-row md:items-center md:gap-10">
          <div className="flex-shrink-0">
            <Gauge score={data.score} descriptor={data.descriptor} />
          </div>
          <div className="mt-5 md:mt-0 flex-1">
            <div className="label-overline text-[color:var(--gold)]/85">Your cosmic score · today</div>
            <h2 className="mt-2 font-serif-display text-[color:var(--parchment)] text-2xl md:text-4xl leading-tight tracking-tight">
              How the field is meeting <span className="font-serif-italic text-[color:var(--gold)]">you</span> today.
            </h2>
            <p className="mt-3 text-[color:var(--parchment-soft)] text-sm md:text-base font-sans-body leading-relaxed">
              A blend of Earth&apos;s Schumann resonance, today&apos;s space weather, and your personal biorhythm.
              Open a card below to see how each is affecting you right now.
            </p>
            <button
              type="button"
              onClick={() => { try { localStorage.removeItem(BIO_KEY); } catch {} setDob(""); setData(null); }}
              data-testid="cosmic-reset-dob"
              className="mt-4 inline-flex items-center gap-2 text-xs label-overline text-[color:var(--text-secondary)] hover:text-[color:var(--gold)]"
            >
              <PenLine size={11} /> not your birthday? update it
            </button>
            <ShareScoreButton dob={dob} score={data.score} descriptor={data.descriptor} />
          </div>
        </div>
      </div>

      {/* Expandable cards */}
      <Accordion type="single" collapsible className="space-y-3" data-testid="cosmic-cards">
        {/* ── Space weather ─────────────────────────────────────── */}
        <AccordionItem
          value="space"
          className="rounded-2xl border border-[color:var(--gold)]/20 px-5 md:px-6"
          style={{ background: "rgba(10, 17, 40, 0.5)" }}
          data-testid="cosmic-card-space"
        >
          <AccordionTrigger className="hover:no-underline py-4 md:py-5">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Sun size={16} className="text-[color:var(--gold)] flex-shrink-0" />
              <div className="text-left min-w-0">
                <div className="label-overline text-[color:var(--gold)]/85">Space weather</div>
                <div className="font-serif-display text-[color:var(--parchment)] text-base md:text-lg truncate">
                  Kp {sw.kp_index ?? "—"} · {sw.kp_status || "—"} · {sw.xray_flux_class || "—"}
                </div>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-5 md:pb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-2">
              {[
                { label: "Kp Index",       value: sw.kp_index ?? "—" },
                { label: "X-Ray Class",    value: sw.xray_flux_class || "—" },
                { label: "Solar Wind",     value: sw.solar_wind_speed ? `${sw.solar_wind_speed} km/s` : "—" },
                { label: "Geomag",         value: sw.geomag_status || "—" },
              ].map((s) => (
                <div key={s.label} className="rounded-lg border border-[color:var(--gold)]/15 p-3">
                  <div className="label-overline text-[color:var(--text-secondary)] text-[10px]">{s.label}</div>
                  <div className="mt-1 font-serif-display text-[color:var(--parchment)] text-lg">{s.value}</div>
                </div>
              ))}
            </div>
            <div className="mt-5 pt-4 border-t border-[color:var(--gold)]/15">
              <div className="label-overline text-[color:var(--gold)]/80 text-[10px]">How it&apos;s affecting you</div>
              <p className="mt-2 text-[color:var(--parchment-soft)] text-sm md:text-base font-sans-body leading-relaxed">
                {sw.impact_note}
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* ── Schumann resonance ───────────────────────────────── */}
        <AccordionItem
          value="schumann"
          className="rounded-2xl border border-[color:var(--gold)]/20 px-5 md:px-6"
          style={{ background: "rgba(10, 17, 40, 0.5)" }}
          data-testid="cosmic-card-schumann"
        >
          <AccordionTrigger className="hover:no-underline py-4 md:py-5">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Waves size={16} className="text-[color:var(--gold)] flex-shrink-0" />
              <div className="text-left min-w-0">
                <div className="label-overline text-[color:var(--gold)]/85">Schumann resonance</div>
                <div className="font-serif-display text-[color:var(--parchment)] text-base md:text-lg truncate">
                  {sc.base_freq_hz ?? "—"} Hz · activity {sc.activity_index ?? "—"}/100 · {sc.status || "—"}
                </div>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-5 md:pb-6">
            <div className="grid grid-cols-3 gap-3 md:gap-4 mt-2">
              {[
                { label: "Base Freq",     value: sc.base_freq_hz != null ? `${sc.base_freq_hz} Hz` : "—" },
                { label: "Amplitude",     value: sc.amplitude ?? "—" },
                { label: "Activity",      value: sc.activity_index != null ? `${sc.activity_index}/100` : "—" },
              ].map((s) => (
                <div key={s.label} className="rounded-lg border border-[color:var(--gold)]/15 p-3">
                  <div className="label-overline text-[color:var(--text-secondary)] text-[10px]">{s.label}</div>
                  <div className="mt-1 font-serif-display text-[color:var(--parchment)] text-lg">{s.value}</div>
                </div>
              ))}
            </div>
            <div className="mt-5 pt-4 border-t border-[color:var(--gold)]/15">
              <div className="label-overline text-[color:var(--gold)]/80 text-[10px]">How it&apos;s affecting you</div>
              <p className="mt-2 text-[color:var(--parchment-soft)] text-sm md:text-base font-sans-body leading-relaxed">
                {sc.impact_note}
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* ── Lunar tracker ─────────────────────────────────────── */}
        {mo && (
          <AccordionItem
            value="moon"
            className="rounded-2xl border border-[color:var(--gold)]/20 px-5 md:px-6"
            style={{ background: "rgba(10, 17, 40, 0.5)" }}
            data-testid="cosmic-card-moon"
          >
            <AccordionTrigger className="hover:no-underline py-4 md:py-5">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Moon size={16} className="text-[color:var(--gold)] flex-shrink-0" />
                <div className="text-left min-w-0">
                  <div className="label-overline text-[color:var(--gold)]/85">Lunar tracker</div>
                  <div className="font-serif-display text-[color:var(--parchment)] text-base md:text-lg truncate">
                    <span className="mr-2" aria-hidden="true">{mo.phase_glyph}</span>
                    {mo.phase} · {Math.round(mo.illumination_pct)}% lit · in {mo.moon_sign}
                  </div>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5 md:pb-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-2">
                {[
                  { label: "Phase",         value: `${mo.phase_glyph || ""} ${mo.phase || "—"}`.trim() },
                  { label: "Illumination",  value: mo.illumination_pct != null ? `${Math.round(mo.illumination_pct)}%` : "—" },
                  { label: "Moon Sign",     value: `${mo.moon_sign_glyph || ""} ${mo.moon_sign || "—"}`.trim() },
                  { label: "Age",           value: mo.age_days != null ? `${mo.age_days} d` : "—" },
                ].map((s) => (
                  <div key={s.label} className="rounded-lg border border-[color:var(--gold)]/15 p-3">
                    <div className="label-overline text-[color:var(--text-secondary)] text-[10px]">{s.label}</div>
                    <div className="mt-1 font-serif-display text-[color:var(--parchment)] text-lg">{s.value}</div>
                  </div>
                ))}
              </div>

              {(mo.next_full_moon || mo.next_new_moon) && (
                <div className="mt-4 grid grid-cols-2 gap-3 md:gap-4">
                  {mo.next_full_moon && (
                    <div className="rounded-lg border border-[color:var(--gold)]/15 p-3">
                      <div className="label-overline text-[color:var(--text-secondary)] text-[10px]">Next full moon</div>
                      <div className="mt-1 font-serif-italic text-[color:var(--parchment)] text-sm md:text-base">
                        {new Date(mo.next_full_moon).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                  )}
                  {mo.next_new_moon && (
                    <div className="rounded-lg border border-[color:var(--gold)]/15 p-3">
                      <div className="label-overline text-[color:var(--text-secondary)] text-[10px]">Next new moon</div>
                      <div className="mt-1 font-serif-italic text-[color:var(--parchment)] text-sm md:text-base">
                        {new Date(mo.next_new_moon).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-5 pt-4 border-t border-[color:var(--gold)]/15">
                <div className="label-overline text-[color:var(--gold)]/80 text-[10px]">How the moon feels today</div>
                <p className="mt-2 text-[color:var(--parchment-soft)] text-sm md:text-base font-sans-body leading-relaxed">
                  {mo.effects}
                </p>
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* ── Personal horoscope ───────────────────────────────── */}
        <AccordionItem
          value="horoscope"
          className="rounded-2xl border border-[color:var(--rose-gold)]/40 px-5 md:px-6"
          style={{ background: "linear-gradient(135deg, rgba(229,168,124,0.10), rgba(212,162,76,0.04))" }}
          data-testid="cosmic-card-horoscope"
        >
          <AccordionTrigger className="hover:no-underline py-4 md:py-5">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Star size={16} className="text-[color:var(--rose-gold)] flex-shrink-0" />
              <div className="text-left min-w-0">
                <div className="label-overline text-[color:var(--rose-gold)]/90">Your horoscope · {ho.sun_sign}</div>
                <div className="font-serif-italic text-[color:var(--parchment)] text-base md:text-lg leading-snug truncate">
                  {ho.opening || "—"}
                </div>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-5 md:pb-6">
            <p className="text-[color:var(--parchment-soft)] text-sm md:text-base font-sans-body leading-relaxed">
              {ho.body}
            </p>
            {ho.one_thing && (
              <div
                className="mt-4 rounded-xl p-4 border border-[color:var(--rose-gold)]/30"
                style={{ background: "rgba(229, 168, 124, 0.08)" }}
              >
                <div className="label-overline text-[color:var(--rose-gold)]/95 text-[10px]">One small thing for today</div>
                <div className="mt-1 font-serif-italic text-[color:var(--parchment)] text-sm md:text-base leading-relaxed">
                  {ho.one_thing}
                </div>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </section>
  );
}
